import { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Divider,
  Link,
  Grid2,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import Cookies from "js-cookie";
import { trackPromise } from "react-promise-tracker";
import apiClient from "../../lib/api";
import urlConstant from "../../constants/urlConstants";
import Spinner from "../../shared-components/spinner";
import dataConstant from "../../constants/dataConstants";
import { selectUserDetail, userDetail } from "../../redux/slice/userSlice";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink,useNavigate } from "react-router-dom";
import { lightPalette } from "../../theme-layout/palette";
import { EmailOutlined, VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const userDetails = useSelector(selectUserDetail)
  const dispatch = useDispatch();
  const navigate = useNavigate()
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token && userDetails?.userId) {
      if(userDetails?.roles === dataConstant.roles.marketer){
        navigate("/submission")
      }else{
        navigate("/dashboard")
      }
    }
  }, [userDetails])

  const toggleVisibility = () => setShowPassword((prev) => !prev);
  const onSubmit = (data) => {
    setLoginError("");
    const requestObject = {
      Password: data.password,
      UserName: data.email,
    };
    trackPromise(
      apiClient
        .post(urlConstant.Login.UserLogin, requestObject, {
          authRequired: false,
        })
        .then((response) => {
          if (response.status === dataConstant.APIStatus.Failure) {
            setLoginError(response.message);
            }else{
                const userDetails = {
                    email: response.data.email,
                    firstName: response.data.firstName,
                    lastName: response.data.lastName,
                    roles: response.data.roles,
                    userId: response.data.userId,
                    phone:response.data.phone,
                    profileImageName:response.data.profileImageName,
                }
                localStorage.setItem("accessToken", response.data.accessToken)
                localStorage.setItem("refreshToken", response.data.refreshToken)
                localStorage.setItem("windowId", response.data.windowId)
                dispatch(userDetail({ userDetail: userDetails }));
                if(response.data.roles === dataConstant.roles.marketer){
                  navigate("/submission")
                }else{
                  navigate("/dashboard")
                }
            }
        })
    );
  };

  return (
    <>
      <Spinner />
      <Grid2 container sx={{ minHeight: "100vh" }}>
        {/* Left side */}
        <Grid2
          item
          size={{xs: 12 , md:7}}
          px={{ md: 8, xs: 3 }}
          py={3}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems={{ xs: "center", md: "flex-start" }}
        >
          <Box maxWidth="520px" width="100%" textAlign={{ xs: "center", md: "left" }}>
            <img
              src="/Cambridge-Caregivers-Logo-2 1.svg"
              alt="Logo"
              className="camcare-logo-svg"
            />
            <Typography variant="h1" color="primary" mt={4}>
              Log in
            </Typography>
            <Divider
              sx={{
                maxWidth: 36,
                borderWidth: 2,
                borderRadius: 4,
                backgroundColor: lightPalette.primary.main,
                mt: 0.5,
                mb: 1,
                mx: { xs: "auto", md: "0" },
              }}
            ></Divider>
            <Typography variant="h6" color="default" mb={3}>
              Welcome back, please log in to continue
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Controller
                name="email"
                control={control}
                rules={{ required: "Email is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <EmailOutlined />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                rules={{ required: "Password is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    onChange={(e) => {
                      field.onChange(e);
                      if (loginError !== "") {
                        setLoginError("")
                      }
                    }}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton color="secondary" onClick={toggleVisibility} edge="end">
                              {showPassword ? (
                                <VisibilityOffOutlined />
                              ) : (
                                <VisibilityOutlined />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />

              <Box textAlign="right" mt={1} mb={2}>
              <Link underline="hover" color="primary" component={RouterLink} to="/forgot-password">
                Forgot password?
              </Link>
            </Box>
              {loginError !== "" && (
                <Typography variant="body2" color="error">
                  {loginError}
                </Typography>
              )}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ borderRadius: 5, py: 1.5, marginTop: "15px" }}
              >
                Log in
              </Button>
            </form>
          </Box>
        </Grid2>

        {/* Right side */}
        <Grid2
          item
          size={{xs: 12 , md:5}}
          display={{ xs: "none", md: "flex" }}
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          padding={4}
          sx={{
            backgroundColor: lightPalette.default.main,
          }}
        >
          <img
            src="/Nursing home-rafiki 1.svg"
            alt="Caregiver Illustration"
            className="camcare-illustration-svg"
          />
          <Typography variant="h3" mt={4} align="center" color="white">
            Welcome to
            <br />
            Cambridge Caregivers
          </Typography>
        </Grid2>
      </Grid2>
    </>
  );
};

export default LoginPage;
